import type { PointsDisplay } from '../../../core/components/canvas/types/canvas-shape.js';
import type { TimeGapPolicy } from '../../../core/types/gap-policy.js';
import type { Timeseries } from '../../../core/types/timeseries.js';
import type { SeriesSlots } from '../iterate-slots.js';
import type { TimeseriesPerShapeInput } from '../transformer-data-to-xy.js';
import type {
  ChartVariant,
  ChartVariantInternal,
  TimeseriesBand,
  TimeseriesChartAreaProps,
  TimeseriesChartBandProps,
  TimeseriesChartBarProps,
  TimeseriesChartLineProps,
} from '../types/timeseries.js';

export interface BuildBucketsResult {
  buckets: TimeseriesPerShapeInput;
  lineAreaBarData: Timeseries[];
  bandData: TimeseriesBand[];
  allSeriesData: (Timeseries | TimeseriesBand)[];
  gapPolicies: (TimeGapPolicy | undefined)[];
  order: ChartVariantInternal[];
}

export function buildBuckets(
  rootData: Timeseries[],
  variant: ChartVariant,
  slots: SeriesSlots,
  defaultPointsDisplay: PointsDisplay | undefined,
): BuildBucketsResult {
  const line: TimeseriesChartLineProps[] = [];
  const area: TimeseriesChartAreaProps[] = [];
  const bar: TimeseriesChartBarProps[] = [];
  const band: TimeseriesChartBandProps[] = [];
  const lineAreaBarData: Timeseries[] = [];
  const bandData: TimeseriesBand[] = [];
  const gapPolicies: (TimeGapPolicy | undefined)[] = [];

  for (const data of rootData) {
    lineAreaBarData.push(data);
    if (variant === 'line') {
      line.push({ data, pointsDisplay: defaultPointsDisplay });
    } else if (variant === 'area') {
      area.push({ data, pointsDisplay: defaultPointsDisplay });
    } else {
      bar.push({ data });
    }
  }

  for (const slot of slots.line) {
    line.push({
      ...slot,
      pointsDisplay: slot.pointsDisplay ?? defaultPointsDisplay,
    });
    lineAreaBarData.push(slot.data);
    gapPolicies.push(slot.gapPolicy);
  }

  for (const slot of slots.area) {
    area.push({
      ...slot,
      pointsDisplay: slot.pointsDisplay ?? defaultPointsDisplay,
    });
    lineAreaBarData.push(slot.data);
    gapPolicies.push(slot.gapPolicy);
  }

  for (const slot of slots.bar) {
    bar.push(slot);
    lineAreaBarData.push(slot.data);
  }

  for (const slot of slots.band) {
    band.push(slot);
    bandData.push(slot.data);
    gapPolicies.push(slot.gapPolicy);
  }

  const order: ChartVariantInternal[] = [...slots.shapePriority];
  if (rootData.length > 0 && !order.includes(variant)) {
    order.push(variant);
  }

  return {
    buckets: { line, area, bar, band },
    lineAreaBarData,
    bandData,
    allSeriesData: [...lineAreaBarData, ...bandData],
    gapPolicies,
    order,
  };
}
